package com.spground.spaceplaygroundwallpaper

import kotlinx.browser.window

/**
 * Web stand-in for WallpaperSettingsActivity's pref constants + SharedPreferences.
 *
 * Defaults here are NOT the Android DEF_* constants: they are the effective
 * values of the shipped "Default" preset (SettingsPresets.defaultInts/Bools
 * merged over the DEF_ baseline), so the web scene opens looking exactly like
 * a fresh install of the app. Values can be overridden per-browser via
 * localStorage using the same key names, prefixed with "sw.".
 */
object WallpaperSettings {

    // Planets / gravity
    const val KEY_GRAVITY_STRENGTH = "gravity_strength";              const val DEF_GRAVITY_STRENGTH = 265
    const val KEY_GRAVITY_RADIUS = "gravity_radius";                  const val DEF_GRAVITY_RADIUS = 48
    const val KEY_PLANET_COUNT = "planet_count";                      const val DEF_PLANET_COUNT = 3
    const val KEY_GRAVITY_BORDER_THICKNESS = "gravity_border_thickness"; const val DEF_GRAVITY_BORDER_THICKNESS = 20
    const val KEY_GRAVITY_BORDER_BRIGHTNESS = "gravity_border_brightness"; const val DEF_GRAVITY_BORDER_BRIGHTNESS = 34
    const val KEY_PLANET_SIZE = "planet_size";                        const val DEF_PLANET_SIZE = 34
    const val KEY_SHOW_PLANETS = "show_planets";                      const val DEF_SHOW_PLANETS = true
    const val KEY_SHOW_GRAVITY_FIELD = "show_gravity_field";          const val DEF_SHOW_GRAVITY_FIELD = true
    const val KEY_GRAVITY_ENABLED = "gravity_enabled";                const val DEF_GRAVITY_ENABLED = true
    const val KEY_PLANET_COLLISION = "planet_collision";              const val DEF_PLANET_COLLISION = false
    const val KEY_PLANET_ROTATION = "planet_rotation";                const val DEF_PLANET_ROTATION = 50
    const val KEY_PLANET_WOBBLE = "planet_wobble";                    const val DEF_PLANET_WOBBLE = 20
    const val KEY_PLANET_GLOW = "planet_glow";                        const val DEF_PLANET_GLOW = 100

    // Rockets
    const val KEY_ROCKET_SPEED = "rocket_speed";                      const val DEF_ROCKET_SPEED = 68
    const val KEY_ROCKET_SPAWN_INTERVAL = "rocket_spawn_interval";    const val DEF_ROCKET_SPAWN_INTERVAL = 5
    const val KEY_ROCKET_GLOW = "rocket_glow";                        const val DEF_ROCKET_GLOW = 100
    const val KEY_ROCKET_SIZE = "rocket_size";                        const val DEF_ROCKET_SIZE = 100
    const val KEY_MAX_LIVE_ROCKETS = "max_live_rockets";              const val DEF_MAX_LIVE_ROCKETS = 0
    const val KEY_DEBRIS_COUNT = "debris_count";                      const val DEF_DEBRIS_COUNT = 10
    const val KEY_DEBRIS_SPIN = "debris_spin";                        const val DEF_DEBRIS_SPIN = 100
    const val KEY_DEBRIS_GLOW = "debris_glow";                        const val DEF_DEBRIS_GLOW = 90

    // Stars
    const val KEY_STAR_COUNT = "star_count";                          const val DEF_STAR_COUNT = 900
    const val KEY_STAR_SIZE = "star_size";                            const val DEF_STAR_SIZE = 100
    const val KEY_STAR_GLOW = "star_glow";                            const val DEF_STAR_GLOW = 130
    const val KEY_STAR_SCROLL = "star_scroll";                        const val DEF_STAR_SCROLL = 40
    const val KEY_CONSTELLATIONS_ENABLED = "constellations_enabled";  const val DEF_CONSTELLATIONS_ENABLED = true
    const val KEY_SHOOTING_STAR_COUNT = "shooting_star_count";        const val DEF_SHOOTING_STAR_COUNT = 3
    const val KEY_SHOOTING_STAR_LENGTH = "shooting_star_length";      const val DEF_SHOOTING_STAR_LENGTH = 100
    const val KEY_SHOOTING_STAR_BRIGHTNESS = "shooting_star_brightness"; const val DEF_SHOOTING_STAR_BRIGHTNESS = 110
    const val KEY_GALAXIES_ENABLED = "galaxies_enabled";              const val DEF_GALAXIES_ENABLED = true
    const val KEY_SPIRAL_GALAXY_COUNT = "spiral_galaxy_count";        const val DEF_SPIRAL_GALAXY_COUNT = 2

    // Glass overlay
    const val KEY_GLASS_TINT = "glass_tint";                          const val DEF_GLASS_TINT = 61
    const val KEY_GLASS_RED = "glass_red";                            const val DEF_GLASS_RED = 0
    const val KEY_GLASS_GREEN = "glass_green";                        const val DEF_GLASS_GREEN = 41
    const val KEY_GLASS_BLUE = "glass_blue";                          const val DEF_GLASS_BLUE = 255
    const val KEY_GLASS_GRADIENT = "glass_gradient";                  const val DEF_GLASS_GRADIENT = 97
    const val KEY_GLASS_ALPHA = "glass_alpha";                        const val DEF_GLASS_ALPHA = 49

    // Effects
    const val KEY_USE_SPRITES = "use_sprites";                        const val DEF_USE_SPRITES = true
    const val KEY_TRUE_BLACK = "true_black";                          const val DEF_TRUE_BLACK = true
    const val KEY_DARK_SCRIM = "dark_scrim";                          const val DEF_DARK_SCRIM = 33
    const val KEY_ELEMENT_DIM = "element_dim";                        const val DEF_ELEMENT_DIM = 71
    const val KEY_CENTER_CLEAR = "center_clear";                      const val DEF_CENTER_CLEAR = 75

    // Lifetimes
    const val KEY_DEBRIS_LIFETIME = "debris_lifetime";                const val DEF_DEBRIS_LIFETIME = 23
    const val KEY_PARTICLE_LIFETIME = "particle_lifetime";            const val DEF_PARTICLE_LIFETIME = 15

    // Warp
    const val KEY_WARP_DURATION = "warp_duration";                    const val DEF_WARP_DURATION = 5
    const val KEY_WARP_INTENSITY = "warp_intensity";                  const val DEF_WARP_INTENSITY = 51

    // Motion
    const val KEY_PARALLAX_AMOUNT = "parallax_amount";                const val DEF_PARALLAX_AMOUNT = 30

    // Battery
    const val KEY_LOW_POWER = "low_power";                            const val DEF_LOW_POWER = false

    private const val PREFIX = "sw."

    fun getInt(key: String, default: Int): Int =
        window.localStorage.getItem(PREFIX + key)?.toIntOrNull() ?: default

    fun getBoolean(key: String, default: Boolean): Boolean =
        when (window.localStorage.getItem(PREFIX + key)) {
            "true" -> true
            "false" -> false
            else -> default
        }

    fun setInt(key: String, value: Int) = window.localStorage.setItem(PREFIX + key, value.toString())

    fun setBoolean(key: String, value: Boolean) = window.localStorage.setItem(PREFIX + key, value.toString())

    /** Drops every override, so the next read falls back to the shipped preset. */
    fun clearAll() {
        val storage = window.localStorage
        val keys = (0 until storage.length).mapNotNull { storage.key(it) }.filter { it.startsWith(PREFIX) }
        keys.forEach { storage.removeItem(it) }
    }

    /** No Material You on the web — always the user-configured RGB. */
    fun resolveGlassColor(): Int = Color.rgb(
        getInt(KEY_GLASS_RED, DEF_GLASS_RED),
        getInt(KEY_GLASS_GREEN, DEF_GLASS_GREEN),
        getInt(KEY_GLASS_BLUE, DEF_GLASS_BLUE),
    )
}
